import React, { useCallback, useLayoutEffect } from 'react';
import Animated from 'react-native-reanimated';
import { StyleSheet } from 'react-native';
import { rem, AppView, withAnimatedNavigationHeader, SCROLL_INDICATOR_INSETS, } from 'design-system';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import StatusBar from 'design-system/StatusBar';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import useRequiredBenefitCallback from 'features/premium/hooks/useRequiredBenefitCallback';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { deleteSmartRule, editSmartRule } from 'actions';
import { setDeviceSettingsKey } from 'actions/deviceSettings';
import { CommonActions } from '@react-navigation/native';
import SmartRuleDetailsCard from '../components/SmartRuleDetailsCard';
import { SmartRuleMenuModal } from '../components/SmartRuleMenuModal';
import SmartRuleMenuButton from '../components/SmartRuleMenuButton';
import { EDIT_SMART_RULE_SUCCESS } from '../actions';
import { selectSmartRuleById } from '../selectors';
const strings = {
    title: 'Rule details',
    button: 'Edit rule',
    buttonInactive: 'Activate',
};
const SmartRuleDetailsScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    const store = useAppStore();
    const smartRule = useAppSelector((store) => selectSmartRuleById(store, route.params.id));
    const { isActive } = smartRule || {};
    const dispatch = useAppDispatch();
    const onEditRule = useCallback(() => {
        navigation.navigateDeprecated('SmartRuleEdit', smartRule);
    }, [smartRule]);
    const onActivateRule = useCallback(() => {
        dispatch(editSmartRule(route.params.id, { isActive: true })).then((res) => {
            if (res?.type === EDIT_SMART_RULE_SUCCESS) {
                navigation.navigateDeprecated('Success', {
                    title: 'Smart rule has been\nactivated!',
                    subTitle: '',
                    onContinue: () => {
                        const state = store.getState();
                        const hasInactiveRules = state.smartRules.rules.some((rule) => !rule.isActive);
                        if (!hasInactiveRules) {
                            navigation.dispatch((navState) => {
                                const routes = [...navState.routes];
                                const filteredRoutes = routes.filter((route) => !(route.name === 'SmartRules' &&
                                    route.params?.showInactive));
                                return CommonActions.reset({
                                    ...navState,
                                    routes: filteredRoutes,
                                    index: filteredRoutes.length - 1,
                                });
                            });
                            setTimeout(() => navigation.pop());
                        }
                        else {
                            navigation.pop();
                        }
                        return true;
                    },
                });
            }
        });
    }, [route.params.id]);
    const onDeactivateRule = useCallback(() => {
        dispatch(editSmartRule(route.params.id, { isActive: false }));
    }, [route.params.id]);
    const onDeleteRule = useCallback(() => {
        dispatch(deleteSmartRule(route.params.id)).then(() => {
            const state = store.getState();
            const remainingRules = state.smartRules.rules || [];
            const hasRules = remainingRules.length > 0;
            const hasInactiveRules = remainingRules.some((rule) => !rule.isActive);
            if (!hasRules) {
                dispatch(setDeviceSettingsKey('didViewSmartRulesIntro', false));
                const hasSmartRulesRoute = navigation
                    .getState()
                    .routes.some((route) => route.name === 'SmartRules');
                if (!hasSmartRulesRoute) {
                    // Reached details without the SmartRules list in the stack
                    // (e.g. from Transactions), so there is nothing to replace.
                    navigation.pop();
                    return;
                }
                navigation.dispatch((navState) => {
                    const routes = [...navState.routes];
                    const smartRulesRoute = routes.findIndex((route) => route.name === 'SmartRules');
                    routes[smartRulesRoute] = {
                        name: 'SmartRuleIntro',
                        key: `SmartRuleIntro-${Date.now()}`,
                    };
                    const filteredRoutes = routes.filter((route) => route.name !== 'SmartRules');
                    return CommonActions.reset({
                        ...navState,
                        routes: filteredRoutes,
                        index: filteredRoutes.length - 1,
                    });
                });
                setTimeout(() => navigation.pop());
            }
            else if (!hasInactiveRules) {
                navigation.dispatch((navState) => {
                    const routes = [...navState.routes];
                    const filteredRoutes = routes.filter((route) => !(route.name === 'SmartRules' && route.params?.showInactive));
                    return CommonActions.reset({
                        ...navState,
                        routes: filteredRoutes,
                        index: filteredRoutes.length - 1,
                    });
                });
                setTimeout(() => navigation.pop());
            }
            else {
                navigation.pop();
            }
        });
    }, [route.params.id, dispatch, navigation, store]);
    const onPress = useRequiredBenefitCallback(useCallback((action) => {
        switch (action) {
            case 'edit':
                onEditRule();
                break;
            case 'activate':
                onActivateRule();
                break;
            case 'deactivate':
                onDeactivateRule();
                break;
            case 'delete':
                onDeleteRule();
                break;
            default:
                break;
        }
    }, [onActivateRule, onDeactivateRule, onDeleteRule, onEditRule]), 'smartRules');
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, styles.container, paddingHorizontalStyle);
    const openMenu = useCallback(() => SmartRuleMenuModal.show({
        onActivate: () => onPress('activate'),
        onEdit: () => onPress('edit'),
        onDelete: onDeleteRule,
        onDeactivate: onDeactivateRule,
        isActive: isActive,
    }), [isActive, onDeactivateRule, onDeleteRule, onPress]);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <SmartRuleMenuButton onPress={openMenu}/>,
        });
    }, [openMenu]);
    if (!smartRule)
        return null;
    return (<AppView withFrame={false}>
        <StatusBar barStyle="dark-content" animated/>
        <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} contentContainerStyle={contentContainerStyle}>
          <SmartRuleDetailsCard item={smartRule}/>
        </Animated.ScrollView>
        <Button brand floating title={isActive ? strings.button : strings.buttonInactive} onPress={() => onPress(isActive ? 'edit' : 'activate')}/>
      </AppView>);
}, ({ colors }) => ({
    title: strings.title,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styles = StyleSheet.create({
    container: {
        paddingTop: rem(16),
    },
});
export default SmartRuleDetailsScreen;
